// Phone-number normalisation for prefilling a form from a stored number.
//
// The engine does not store one shape. The same field arrives as `+254798676788`,
// `00254729874002`, `0729874002` or `729874002` depending on which journey wrote
// it. ASPin's claimant form normalised by slicing a fixed five characters off the
// front — right for `00254…`, wrong for `+254…`, where it produced an 8-digit
// number that then failed the form's own 9-digit check (OMK retest, item 13).
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

/** Every non-digit stripped. */
export function phoneDigits(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).replace(/\D/g, '');
}

/**
 * The national significant number — the digits a user types locally, with any
 * international or trunk prefix removed.
 *
 * `countryCode` defaults to Kenya's 254. Unknown prefixes are left alone rather
 * than guessed at, so an international number survives instead of being mangled.
 */
export function nationalPhoneNumber(value, countryCode) {
  const cc = phoneDigits(countryCode) || '254';
  let digits = phoneDigits(value);
  if (!digits) {
    return '';
  }
  // 00 is the international access prefix — the stored equivalent of a leading +.
  if (digits.indexOf('00' + cc) === 0) {
    digits = digits.substring(2 + cc.length);
  } else if (digits.indexOf(cc) === 0 && digits.length > cc.length) {
    digits = digits.substring(cc.length);
  } else if (digits.indexOf('00') === 0) {
    digits = digits.substring(2);
  }
  // A domestic trunk prefix, e.g. 0712345678 -> 712345678.
  if (digits.length > 9 && digits.charAt(0) === '0') {
    digits = digits.substring(1);
  } else if (digits.length === 10 && digits.charAt(0) === '0') {
    digits = digits.substring(1);
  }
  return digits;
}

/**
 * The number in the form the engine stores, `00` + country code + national
 * number. Used when writing back, so what we send matches what is already there.
 */
export function toStoredPhoneNumber(value, countryCode) {
  const cc = phoneDigits(countryCode) || '254';
  const digits = phoneDigits(value);
  if (!digits) {
    return '';
  }
  // Already stored-shaped, or already carries the country code.
  if (digits.indexOf('00' + cc) === 0) {
    return digits;
  }
  if (digits.indexOf(cc) === 0 && digits.length > cc.length) {
    return '00' + digits;
  }
  // An international number for some other country — keep it, just prefix the
  // access code rather than forcing it into the local one.
  if (digits.indexOf('00') === 0) {
    return digits;
  }
  return '00' + cc + nationalPhoneNumber(digits, cc);
}
